<template>
  <div class="company-export-page">
    <TableList
      v-rect-loading="loading"
      class="company-export-list"
      ref="tableList"
      noAction
      :title="$t(`${i18nPrefix}.title`)"
      :listData="listData"
      :tableColumns="tableColumns"
      :showCreateBtn="false"
    >
      <template #filter>
        <el-date-picker
          v-model="startAndEndDate"
          class="start-end-date-picker"
          type="datetimerange"
          range-separator="-"
          format="yyyy-MM-dd HH:mm"
          value-format="yyyy-MM-dd HH:mm"
          align="right"
          unlink-panels
          :clearable="false"
          :start-placeholder="$t(`${i18nPrefix}.label.startDate`)"
          :end-placeholder="$t(`${i18nPrefix}.label.endDate`)"
          :picker-options="pickerOptions"
        >
        </el-date-picker>
      </template>
      <template #export-btn="{data}">
        <el-button v-rect-loading="data.loading" class="export-btn" @click="data.func">{{ $t(`${i18nPrefix}.label.export`) }}</el-button>
      </template>
    </TableList>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import moment from 'moment'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.company.exportData',
      loading: false,
      startAndEndDate: [],
      account: null,
      exportRegistrationListTask: {
        loading: false,
        task: null
      },
      exportJobApplicationListTask: {
        loading: false,
        task: null
      },
      exportReferralListTask: {
        loading: false,
        task: null
      },
      exportCommentListTask: {
        loading: false,
        task: null
      },
      exportStatusChangeListTask: {
        loading: false,
        task: null
      },
      exportGroupListTask: {
        loading: false,
        task: null
      },
      exportHrLoginTask: {
        loading: false,
        task: null
      },
      exportJobPostingHistoryTask: {
        loading: false,
        task: null
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.name`),
          dataKey: 'name',
          width: '150px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.operator`),
          dataKey: 'handleExport',
          slotName: 'export-btn',
          width: '150px'
        }
      ]
      return columns
    },
    listData () {
      return [
        {
          name: this.$t(`${this.i18nPrefix}.label.registration`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportRegistrationListTask, 'userList'),
            loading: this.exportRegistrationListTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.application`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportJobApplicationListTask, 'jobApplicantList'),
            loading: this.exportJobApplicationListTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.referral`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportReferralListTask, 'referralList'),
            loading: this.exportReferralListTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.comment`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportCommentListTask, 'commentList'),
            loading: this.exportCommentListTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.statusChange`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportStatusChangeListTask, 'statusChangeList'),
            loading: this.exportStatusChangeListTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.group`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportGroupListTask, 'groupList'),
            loading: this.exportGroupListTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.hrLogin`),
          handleExport: {
            func: () => this.exportDateReportByType(this.exportHrLoginTask, 'hrLoginList'),
            loading: this.exportHrLoginTask.loading
          }
        },
        {
          name: this.$t(`${this.i18nPrefix}.label.jobPostingHistory`),
          handleExport: {
            func: this.exportJobPostingHistoryReport,
            loading: this.exportJobPostingHistoryTask.loading
          }
        }
      ]
    },
    pickerOptions () {
      return {
        shortcuts: [
          {
            text: this.$t(`${this.i18nPrefix}.label.today`),
            onClick (picker) {
              const end = moment().startOf('day').add(1, 'days').format('YYYY-MM-DD HH:mm')
              const start = moment().startOf('day').format('YYYY-MM-DD HH:mm')
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: this.$t(`${this.i18nPrefix}.label.yesterday`),
            onClick (picker) {
              const end = moment().startOf('day').format('YYYY-MM-DD HH:mm')
              const start = moment().startOf('day').add(-1, 'days').format('YYYY-MM-DD HH:mm')
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: this.$t(`${this.i18nPrefix}.label.last7Days`),
            onClick (picker) {
              const end = moment().startOf('day').format('YYYY-MM-DD HH:mm')
              const start = moment().startOf('day').add(-7, 'days').format('YYYY-MM-DD HH:mm')
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: this.$t(`${this.i18nPrefix}.label.last30Days`),
            onClick (picker) {
              const end = moment().startOf('day').format('YYYY-MM-DD HH:mm')
              const start = moment().startOf('day').add(-30, 'days').format('YYYY-MM-DD HH:mm')
              picker.$emit('pick', [start, end])
            }
          }
        ],
        disabledDate: (time) => {
          return time.getTime() > moment().add(1, 'days')
        }
      }
    }
  },
  created () {
    this.account = this.getCurrentAccount()
    this.initData()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('chartReport', [
      'createExportCompanyDataReportByTypeTask',
      'getExportCompanyDataReportByTypeTaskStatus',
      'createExportJobPostingHistoryReportTask',
      'getExportJobPostingHistoryReportTaskStatus'
    ]),
    async initData () {
      this.loading = true
      try {
      } finally {
        this.loading = false
      }
    },
    validateParams () {
      let valid = true

      if (_.isEmpty(this.startAndEndDate)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.dateRangeRequired`))
        valid = false
      }
      if (valid && _.isEqual(this.startAndEndDate[0], this.startAndEndDate[1])) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.dateRangeError`))
        valid = false
      }

      if (!valid) {
        this.$el.querySelector('.list-head').scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
      return valid
    },
    exportJobPostingHistoryReport () {
      const exportJobPostingHistoryTask = this.exportJobPostingHistoryTask
      if (!this.validateParams() || exportJobPostingHistoryTask.loading) {
        return
      }
      exportJobPostingHistoryTask.loading = true
      exportJobPostingHistoryTask.task = new AsyncTask(() => this.createExportJobPostingHistoryReportTask({
        start_time: this.startAndEndDate[0],
        end_time: this.startAndEndDate[1],
        all_company: true,
        company_ids: [],
        include_sub_company: true
      }).catch((e) => {
        exportJobPostingHistoryTask.loading = false
        this.$message.error(e)
      }), (taskId) => this.getExportJobPostingHistoryReportTaskStatus({ task_id: taskId }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/export-job-posting-history-report/?task_id=${taskId}&download=1`)
        exportJobPostingHistoryTask.task = null
        exportJobPostingHistoryTask.loading = false
      })
    },
    exportDateReportByType (task, dataType) {
      if (!this.validateParams() || task.loading) {
        return
      }
      task.loading = true
      task.task = new AsyncTask(() => this.createExportCompanyDataReportByTypeTask({
        start_time: this.startAndEndDate[0],
        end_time: this.startAndEndDate[1],
        data_type: dataType
      }).catch((e) => {
        task.loading = false
        this.$message.error(e)
      }), (taskId) => this.getExportCompanyDataReportByTypeTaskStatus({ task_id: taskId }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/chart-report/export-excel/export-data/?task_id=${taskId}&download=1`)
        task.task = null
        task.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.company-export-page {
  .company-export-list {
    .company-filter {
      display: flex;
      align-items: center;
      margin-right: 20px;

      .text {
        margin-right: 10px;
      }

      .company-select {
        ::v-deep {
          .el-select {
            .el-input__inner {
              height: 32px;
              line-height: 32px;
            }
          }
        }
      }
    }

    .start-end-date-picker {
      width: 340px;
      height: 32px;
      line-height: 32px;
      border-radius: 4px;
      margin-right: 20px;

      ::v-deep {
        .el-range__icon {
          line-height: 24px;
        }

        .el-range-separator {
          line-height: 24px;
        }
      }
    }

    .sub-company-status {
      .text {
        margin-right: 10px;
      }
    }

    .export-btn {
      padding: 0;
      width: 100px;
      height: 36px;
      line-height: 36px;
      color: #fff;
      border-radius: 4px;
      border: 1px solid #0096cc;
      background-color: #0096cc;

      ::v-deep {
        .el-loading-mask {
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          width: auto;
          height: auto;

          .el-loading-spinner {
            margin-top: -14px;
            .spinner {
              height: 26px;
            }
          }
        }
      }
    }
  }

  ::v-deep {
    .list-head {
      height: auto;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-bottom: 10px;

      .title {
        margin-bottom: 10px;
      }

      .filter-container {
        margin-bottom: 10px;
      }

      .btn-container {
        display: flex;
        margin-left: 10px;
        margin-bottom: 10px;
      }
    }

    .el-dialog {
      width: 591px;
    }

    .el-dialog__body {
      padding: 30px 40px;
    }
  }
}
</style>
